
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { ThemeProvider } from "./context/ThemeContext";
import { TimerProvider } from "./context/TimerContext";
import { AuthProvider } from "./context/AuthContext";
import { ListProvider } from "./context/ListContext";
import { TaskProvider } from "./context/TaskContext";
import { ErrorBoundary } from "./components/ErrorBoundary";
import Index from "./pages/Index";
import NotFound from "./pages/NotFound";
import EmailConfirmation from "./pages/EmailConfirmation";
import OnboardingModal from "./components/onboarding/OnboardingModal";
import { useOnboarding } from "./hooks/useOnboarding";
import { useEffect } from "react";
import { TaskDetailModalProvider } from "./components/tasks/TaskDetailModalContext";
import { TaskDetailModal } from "./components/tasks/TaskDetailModal";
import TasksPage from "./pages/TasksPage";
import Kanban from "./pages/Kanban";

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      staleTime: 5 * 60 * 1000, // 5 minutes
      gcTime: 10 * 60 * 1000, // 10 minutes
      refetchOnWindowFocus: false,
    },
  },
});

const AppContent = () => {
  const { showOnboarding, completeOnboarding } = useOnboarding();

  return (
    <>
      <Routes>
        <Route path="/" element={<Index />} />
        {/* Restore /kanban to show edu content, NOT TasksPage */}
        <Route path="/kanban" element={<Kanban />} />
        {/* New hybrid: /tasks is main full list+tasks management */}
        <Route path="/tasks" element={<TasksPage />} />
        <Route path="/pomodoro" element={<Index />} />
        <Route path="/eisenhower" element={<Index />} />
        <Route path="/confirm-email" element={<EmailConfirmation />} />
        <Route path="*" element={<NotFound />} />
      </Routes>
      <OnboardingModal 
        isOpen={showOnboarding}
        onComplete={completeOnboarding}
      />
      <TaskDetailModal />
    </>
  );
};

const App = () => {
  // Remove prerendered content after hydration
  useEffect(() => {
    try {
      const prerenderContent = document.querySelector('.prerender-content');
      if (prerenderContent) {
        prerenderContent.remove();
      }
    } catch (error) {
      console.warn('Failed to remove prerender content:', error);
    }
  }, []);
  
  return (
    <ErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <TooltipProvider>
          <BrowserRouter>
            <AuthProvider>
              <ThemeProvider>
                <ListProvider>
                  <TaskProvider>
                    <TaskDetailModalProvider>
                      <TimerProvider>
                        <Toaster />
                        <Sonner />
                        <AppContent />
                      </TimerProvider>
                    </TaskDetailModalProvider>
                  </TaskProvider>
                </ListProvider>
              </ThemeProvider>
            </AuthProvider>
          </BrowserRouter>
        </TooltipProvider>
      </QueryClientProvider>
    </ErrorBoundary>
  );
};

export default App;
